/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { ReactNode } from "react";

import { useTranslation } from "react-i18next";

import { useTableStoreByRoute } from "@/stores/useTableStore";

import { ROUTES, TableRowData, useMediaQueries } from "../../../../shared";
import Checkbox from "../../../CheckBox/Checkbox";
import TextLink from "../../../TextLink/TextLink";
import { TEXT_LINK_SIZES } from "../../../TextLink/types";
import {
  generateTableToolbarStyle,
  tableHeadToolbarDivStyle
} from "../../styles/styleFunctions";

interface TableHeadToolbarProps {
  route: ROUTES;
  children: ReactNode;
}

const TableHeadToolbar = ({ route, children }: TableHeadToolbarProps) => {
  const { t } = useTranslation();
  const { toMd, fromMd } = useMediaQueries();

  const { selectableRows, selectedRows, setSelectedRows, clearSelection } =
    useTableStoreByRoute(route);

  const rowsCount = selectedRows.length;
  const visibleTableItemsIdArray = selectableRows.map(
    (row: TableRowData) => row.id as string
  );

  const allSelected = visibleTableItemsIdArray.every(id =>
    selectedRows.includes(id)
  );

  const atLeastSingleSelected = selectedRows.some(id =>
    visibleTableItemsIdArray.includes(id)
  );

  const visibleTableIds = selectableRows.map(row => row.id as string);

  const handleTableHeadCheckboxChange = () => {
    if (atLeastSingleSelected) {
      // Some or all visible rows are selected → deselect them
      setSelectedRows(selectedRows.filter(id => !visibleTableIds.includes(id)));
    } else {
      // None selected → select all visible rows
      setSelectedRows(
        Array.from(new Set([...selectedRows, ...visibleTableIds]))
      );
    }
  };

  return (
    <div css={css(generateTableToolbarStyle())}>
      <div css={css(tableHeadToolbarDivStyle(toMd))}>
        <Checkbox
          id={`${route}-rows-selected`}
          label={t("Button##rows selected", {
            count: rowsCount
          })}
          checked={allSelected}
          indeterminate={!allSelected && atLeastSingleSelected}
          onChange={handleTableHeadCheckboxChange}
        />
      </div>

      {children}

      {fromMd && (
        <TextLink
          component="span"
          size={TEXT_LINK_SIZES.Normal}
          onClick={clearSelection}
          noFlex
          customStyle={{
            alignContent: "center",
            marginLeft: "16px",
            flex: "1",
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap"
          }}
        >
          {t("Button##deselect all")}
        </TextLink>
      )}
    </div>
  );
};

export default TableHeadToolbar;
